import { ImagePlus, X } from 'lucide-react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Button } from '@nucleus-commerce/ui/components/button';

import { MediaPickerDialog } from '@/features/media';

import type { CollectionFormInput } from '../schemas/collection-form-schema';

import { CollectionThumb } from './collection-thumb';

/** One image from the media library; the picker is the shared dialog, first pick wins. */
export function CollectionImageField() {
  const { t } = useTranslation('collections');
  const form = useFormContext<CollectionFormInput>();
  const mediaId = form.watch('mediaId');
  const [open, setOpen] = useState(false);

  return (
    <FormSection
      title={t('sections.image')}
      description={t('sections.imageDescription')}
      tour="collections.edit.image"
    >
      <div className="flex items-center gap-4">
        <CollectionThumb mediaId={mediaId} className="size-24 shrink-0 rounded-md border" />
        <div className="flex flex-col items-start gap-2">
          {mediaId ? null : (
            <span className="text-muted-foreground text-sm">{t('image.none')}</span>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
              <ImagePlus aria-hidden />
              {mediaId ? t('image.change') : t('image.choose')}
            </Button>
            {mediaId ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => form.setValue('mediaId', null, { shouldDirty: true })}
              >
                <X aria-hidden />
                {t('image.remove')}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <MediaPickerDialog
        open={open}
        onOpenChange={setOpen}
        kind="image"
        multiple={false}
        onPick={(picked) => {
          const first = picked[0];
          if (first) form.setValue('mediaId', first.id, { shouldDirty: true });
        }}
      />
    </FormSection>
  );
}
